import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/announcements", label: "Announcements" },
    { to: "/policies", label: "Policies" },
    { to: "/book", label: "Book" },
];

const Brand = ({ settings, brand, light }) => (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
        {settings?.logo_url ? (
            <img src={settings.logo_url} alt="logo" className="h-10 w-10 rounded-xl object-cover ring-1 ring-black/5" />
        ) : (
            <div className={`h-10 w-10 rounded-xl grid place-items-center font-display text-lg ${light ? "bg-white !text-[#0A1F5C]" : "bg-[hsl(var(--primary))] text-white"}`}>
                {brand[0]}
            </div>
        )}
        <div className="leading-tight">
            <div className={`font-display text-base sm:text-lg ${light ? "!text-white" : ""}`}>{brand}</div>
            <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${light ? "text-white/60" : "text-[hsl(var(--accent))]"}`}>
                Financial services
            </div>
        </div>
    </Link>
);

const Header = ({ settings, brand }) => {
    const [open, setOpen] = useState(false);
    return (
        <header className="glass-nav sticky top-0 z-40" data-testid="public-header">
            <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                <Brand settings={settings} brand={brand} />
                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link to="/book" data-testid="nav-book-cta" className="ml-3 pill btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5">
                        Book appointment <ArrowUpRight size={15} />
                    </Link>
                </nav>
                <button onClick={() => setOpen((v) => !v)} className="md:hidden p-2 rounded-lg hover:bg-[hsl(var(--secondary))]" aria-label="Toggle menu" data-testid="mobile-menu-toggle">
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>
            {open && (
                <div className="md:hidden px-6 pb-5 flex flex-col gap-1 border-t border-[hsl(var(--border))] bg-white">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.label.toLowerCase()}`} className="py-3 text-sm font-semibold border-b border-[hsl(var(--border))]">
                            {l.label}
                        </NavLink>
                    ))}
                    <Link to="/book" onClick={() => setOpen(false)} data-testid="nav-mobile-book-cta" className="mt-4 pill btn-primary w-full py-4 text-base font-semibold inline-flex items-center justify-center gap-2">
                        Book appointment <ArrowUpRight size={18} />
                    </Link>
                </div>
            )}
        </header>
    );
};

const Footer = ({ settings, brand }) => (
    <footer className="navy-panel mt-24" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-12 gap-10 text-sm">
            <div className="md:col-span-5">
                <Brand settings={settings} brand={brand} light />
                <p className="mt-5 text-white/70 max-w-sm leading-relaxed">
                    {settings?.tagline || "Personalized financial guidance for a confident future."}
                </p>
                <Link to="/book" data-testid="footer-book-btn" className="mt-6 pill inline-flex items-center gap-2 bg-white text-[#0A1F5C] px-5 py-2.5 font-semibold hover:bg-blue-50 transition-colors">
                    Book appointment <ArrowUpRight size={15} />
                </Link>
            </div>
            <div className="md:col-span-4">
                <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Contact</div>
                <div className="mt-4 space-y-3 text-white/85">
                    {settings?.contact_email && <div className="flex items-center gap-3"><Mail size={15} className="text-blue-300" />{settings.contact_email}</div>}
                    {settings?.contact_phone && <div className="flex items-center gap-3"><Phone size={15} className="text-blue-300" />{settings.contact_phone}</div>}
                    {settings?.address && <div className="flex items-start gap-3"><MapPin size={15} className="text-blue-300 mt-0.5" />{settings.address}</div>}
                </div>
            </div>
            <div className="md:col-span-3">
                <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Explore</div>
                <div className="mt-4 flex flex-col gap-2.5 text-white/85">
                    {links.slice(1).map((l) => (
                        <Link key={l.to} to={l.to} className="hover:text-white transition-colors">{l.label}</Link>
                    ))}
                    <Link to="/admin" className="hover:text-white transition-colors text-white/60" data-testid="footer-admin-link">Staff sign-in</Link>
                </div>
            </div>
        </div>
        <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-white/50">
                © {new Date().getFullYear()} {brand}. All rights reserved.
            </div>
        </div>
    </footer>
);

export const PublicLayout = ({ children, settings }) => {
    const brand = settings?.business_name || "Joseph Kabir Financial";
    return (
        <div className="theme-public min-h-screen flex flex-col bg-white text-[hsl(var(--foreground))]">
            <Header settings={settings} brand={brand} />
            <main className="flex-1">{children}</main>
            <Footer settings={settings} brand={brand} />
        </div>
    );
};
